/* eslint-disable camelcase */
// One row shape for both kinds of report schedule.
//
// The unified list endpoint is a single queryset over a single model
// (PracticeReportScheduleSettings), so pagination, sorting and the total are
// genuine and this layer does no merging of two sources. All it does is name
// the fields the table renders, so that a contract change is a change here.
//
// Contract: docs/contracts/client-reports-v1.md, "Unified schedules list".

import { isClientReport } from '../clientReports/clientReportModel';

export const LEGACY = 'legacy';
export const CLIENT = 'client';

export const KIND_LABELS = {
    [LEGACY]: 'CRM report',
    [CLIENT]: 'Client advertising report',
};

// A row's kind decides which actions it offers and which endpoint a write
// goes to. The server always sends it; the report-type fallback only covers a
// response that predates the discriminator.
export const rowKind = (row) => {
    if (row?.kind === LEGACY || row?.kind === CLIENT) return row.kind;
    return isClientReport(row?.report_type) ? CLIENT : LEGACY;
};

const asArray = (value) => (Array.isArray(value) ? value : []);

export function normaliseScheduleRow(row) {
    const kind = rowKind(row);
    return {
        ...row,
        kind,
        // Every key is present on both kinds. Only next_run_at (null when the
        // schedule is disabled) and latest_result (null when nothing has run)
        // are ever null, so no column needs an "inapplicable" branch: a CRM
        // schedule has one location, one output format and the London zone
        // the legacy scheduler is fixed to, just as a client report has
        // several locations and its own zone.
        locations: asArray(row?.locations),
        formats: asArray(row?.formats),
        period: row?.period ?? '',
        periodDisplay: row?.period_display || row?.period || '',
        reportTypeDisplay: row?.report_type_display || row?.report_type || '',
        timezone: row?.timezone || '',
        recipientsCount: Number.isFinite(row?.recipients_count) ? row.recipients_count : 0,
        isEnabled: Boolean(row?.is_enabled),
        latestResult: row?.latest_result || null,
    };
}

export const normaliseScheduleRows = (results) => asArray(results).map(normaliseScheduleRow);

// Which of the row-level actions each kind supports. An action that does not
// apply is not rendered at all: a permanently disabled icon reads as broken.
// Client run history is immutable by contract, hence no delete.
export const rowActions = (kind) =>
    kind === CLIENT
        ? { edit: true, history: true, testSend: true, remove: false }
        : { edit: true, history: true, testSend: true, remove: true };
